/**
 * Centrale bedrijfsgegevens + helpers voor SEO/GEO/AEO-metadata.
 * Gebruikt in head() van elke route en in de zichtbare contactblokken.
 */

import { DEFAULT_LANG, LANGS, isLang, type Lang } from "@/i18n/config";

export const SITE_URL = "https://goproprojects.be";

export const business = {
  name: "Gopro Projects",
  legalName: "Gopro Projects",
  street: "Slakweidestraat 42",
  postalCode: "3630",
  city: "Maasmechelen",
  region: "Limburg",
  country: "BE",
  vat: "BE 0748439033",
  phone: "0484 97 64 77",
  phoneE164: "+32484976477",
  email: "info@goproprojects.be",
  areaServed: ["Limburg", "Zuid-Nederland"],
  areaLabel: "Limburg & Zuid-Nederland",
  responseTimeHours: 24,
} as const;

export const responseTime = {
  short: `${business.responseTimeHours} uur`,
  within: `binnen ${business.responseTimeHours} uur`,
} as const;

export const addressLine = `${business.street}, ${business.postalCode} ${business.city}`;

export function canonical(path: string) {
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

/** Standaard head-onderdelen voor een pagina: og:url + canonical. */
export function pageUrlMeta(path: string) {
  return {
    meta: [{ property: "og:url", content: canonical(path) }],
    links: [{ rel: "canonical", href: canonical(path) }],
  };
}


/**
 * Deelafbeelding (1200x630) voor Open Graph / Twitter.
 * Bestanden staan in public/og/ zodat de URL absoluut en stabiel is.
 */
export function shareImageMeta(name: string, alt: string) {
  const url = `${SITE_URL}/og/${name}.jpg`;
  return [
    { property: "og:image", content: url },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: alt },
    { name: "twitter:image", content: url },
  ];
}

/** Deelafbeelding op basis van een pad of volledige URL (voor inhoud uit de databank). */
export function shareImageMetaFromPath(path: string, alt: string) {
  const url = path.startsWith("http") ? path : `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
  return [
    { property: "og:image", content: url },
    { property: "og:image:alt", content: alt },
    { name: "twitter:image", content: url },
  ];
}

/** LocalBusiness JSON-LD — enkel in __root.tsx. */
export function localBusinessJsonLd() {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#organization`,
      name: business.name,
      url: SITE_URL,
      telephone: business.phoneE164,
      email: business.email,
      vatID: business.vat,
      address: {
        "@type": "PostalAddress",
        streetAddress: business.street,
        postalCode: business.postalCode,
        addressLocality: business.city,
        addressRegion: business.region,
        addressCountry: business.country,
      },
      areaServed: business.areaServed.map((a) => ({ "@type": "AdministrativeArea", name: a })),
      description:
        "Gopro Projects bouwt bouwkundige zwembaden van A tot Z en is gespecialiseerd in de renovatie van polyester monoblock zwembaden, in Limburg en Zuid-Nederland.",
      knowsAbout: [
        "bouwkundig zwembad",
        "polyester zwembadbekleding",
        "vinylester zwembadbekleding",
        "monoblock zwembad renovatie",
        "osmose zwembad",
        "zwembad lekdetectie",
        "zwembadrolluik",
        "zwembadoverkapping",
        "warmtepomp zwembad",
      ],
    }),
  };
}

/** Service JSON-LD voor een dienstenpagina. */
export function serviceJsonLd(input: { name: string; description: string; path: string; serviceType: string }) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: input.name,
      serviceType: input.serviceType,
      description: input.description,
      url: canonical(input.path),
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: business.areaServed.map((a) => ({ "@type": "AdministrativeArea", name: a })),
    }),
  };
}

/** Kruimelpad JSON-LD voor een subpagina. */
export function breadcrumbJsonLd(name: string, path: string) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: canonical("/") },
        { "@type": "ListItem", position: 2, name, item: canonical(path) },
      ],
    }),
  };
}

/** Pad met taalprefix, bv. localizedPath("fr", "/offerte") -> "/fr/offerte". */
export function localizedPath(lang: string, path: string) {
  return path === "/" ? `/${lang}` : `/${lang}${path}`;
}

/**
 * Canonical + hreflang voor een publieke pagina in één taal.
 * Het pad is het taalloze pad, bv. "/technieken".
 */
export function langPageMeta(lang: string, path: string) {
  const active: Lang = isLang(lang) ? lang : DEFAULT_LANG;
  const self = canonical(localizedPath(active, path));
  return {
    meta: [{ property: "og:url", content: self }],
    links: [
      { rel: "canonical", href: self },
      ...LANGS.map((code) => ({
        rel: "alternate",
        hrefLang: code,
        href: canonical(localizedPath(code, path)),
      })),
      { rel: "alternate", hrefLang: "x-default", href: canonical(localizedPath(DEFAULT_LANG, path)) },
    ],
  };
}
